import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CertificateTemplateEditor,
  initialPlaceholders,
  Input,
} from '@mlm/ui';
import {
  createTemplate,
  deleteTemplate,
  listTemplates,
  templateImageUrl,
  updateTemplate,
  type CertificatePlaceholder,
} from '../lib/api';

/**
 * Certificate templates — the creator uploads a background image and places
 * text fields on it (drag on the preview, or edit the numbers). The learner
 * name placeholder is what makes each certificate personal; the layout is
 * snapshotted onto every issued certificate.
 */
export function CertificatesPage() {
  const qc = useQueryClient();
  const { data: templates = [] } = useQuery({
    queryKey: ['creator-templates'],
    queryFn: listTemplates,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = templates.find((t) => t.id === selectedId) ?? null;
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);

  const save = useMutation({
    meta: { successMessage: 'Template saved.' },
    mutationFn: (v: {
      id: string;
      name: string;
      config: { placeholders: CertificatePlaceholder[] };
      imageFile: File | null;
    }) => updateTemplate(v.id, { name: v.name, config: v.config }, v.imageFile),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['creator-templates'] }),
  });
  const remove = useMutation({
    meta: { successMessage: 'Template deleted.' },
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator-templates'] });
      setSelectedId(null);
    },
  });

  const create = useMutation({
    meta: { successMessage: 'Template created — now place the fields.' },
    mutationFn: () => createTemplate(newName, newFile!, { placeholders: initialPlaceholders() }),
    onSuccess: (row) => {
      setNewName('');
      setNewFile(null);
      qc.invalidateQueries({ queryKey: ['creator-templates'] });
      setSelectedId(row.id);
    },
  });

  return (
    <div className="max-w-5xl space-y-4">
      <h1 className="text-2xl font-semibold">Certificate templates</h1>
      <p className="text-sm text-muted-foreground">
        Upload a certificate design (PNG/JPEG, landscape works best) and place the learner's name on
        it. Assign a template to a course from its editor — learners receive it when they finish.
      </p>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">New template</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name"
              />
              <Input
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => setNewFile(e.target.files?.[0] ?? null)}
              />
              <Button
                size="sm"
                className="w-full"
                disabled={!newName || !newFile || create.isPending}
                onClick={() => create.mutate()}
              >
                Upload
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-0">
              {templates.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No templates yet.</p>
              ) : (
                <ul>
                  {templates.map((t) => (
                    <li key={t.id}>
                      <button
                        onClick={() => setSelectedId(t.id)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-muted/40 ${t.id === selectedId ? 'bg-accent font-medium' : ''}`}
                      >
                        {t.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
        <div>
          {selected ? (
            <CertificateTemplateEditor
              key={selected.id}
              templateId={selected.id}
              name={selected.name}
              // `?v=<imageKey>` busts the 5-minute asset cache the moment a
              // background is replaced; the key changes on every upload.
              imageUrl={`${templateImageUrl(selected.id)}?v=${encodeURIComponent(selected.imageKey)}`}
              placeholders={selected.config.placeholders}
              saving={save.isPending}
              deleting={remove.isPending}
              onSave={(next) => save.mutate({ id: selected.id, ...next })}
              onDelete={() => remove.mutate(selected.id)}
            />
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Select a template to edit its layout.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
